/**
 * Physical Therapy Visit Note (Follow-Up) — options + empty payload.
 * After shared common header: Reason → Subjective → Objective →
 * Interventions → Response → Functional Status → Goals → Plan → Notes
 * (per product screenshots). No wound section. No separate Visit Information block.
 */

import { emptyPtNotesCommonHeader } from "./ptNotesCommonHeaderUtils";
import { emptyTextGoals } from "./ptGoals";

export { PAIN_LEVELS } from "./ptClinicalVitals";

export const YES_NO_OPTIONS = ["Yes", "No"];

export const YES_NO_SOME_OPTIONS = ["Yes", "No", "Some"];

export const VISIT_TYPE_OPTIONS = ["Skilled", "Reassessment"];

export const REASON_FOR_VISIT_OPTIONS = [
  "Routine Follow-Up",
  "Progress Check",
  "Other",
];

export const BALANCE_OPTIONS = ["Good", "Fair", "Poor"];

export const GAIT_MOBILITY_OPTIONS = [
  "Independent",
  "With Device",
  "Supervision",
  "CGA",
];

export const ASSISTIVE_DEVICE_OPTIONS = [
  "None",
  "SPC",
  "Standard Cane",
  "Quad Cane",
  "FWW",
  "4WW",
  "Rollator",
  "Wheelchair",
  "Other",
];

export const PT_VISIT_INTERVENTION_OPTIONS = [
  "Therapeutic Exercise",
  "Gait Training",
  "Therapeutic Activities",
  "Balance Training",
  "Transfer Training",
  "Manual Therapy",
  "HEP Education",
  "Patient / Caregiver Education",
  "Other",
];

export const PATIENT_RESPONSE_OPTIONS = ["Good", "Fair", "Poor"];

export const PROGRESS_TOWARD_GOALS_OPTIONS = [
  "On Track",
  "Progressing",
  "Plateau",
  "Regression",
];

export const MOBILITY_TREND_OPTIONS = ["Improved", "Same", "Declined"];

export const TRANSFER_STATUS_OPTIONS = [
  "Independent",
  "Modified Independent",
  "Supervision",
  "CGA",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Dependent",
];

export const AMBULATION_STATUS_OPTIONS = [
  "Independent",
  "With Device (Supervision)",
  "With Device (CGA)",
  "Min Assist",
  "Mod Assist",
  "Max Assist",
  "Non-ambulatory",
];

export const ADL_IMPACTED_OPTIONS = [
  "Bathing",
  "Dressing",
  "Toileting",
  "Stairs",
];

export const HOME_SAFETY_OPTIONS = [
  "Safe",
  "Needs Improvement",
  "Needs Education",
];

export const MMT_SIDE_OPTIONS = ["R", "L", "Bilat"];

export const DEFAULT_MMT_MUSCLES = ["Quads", "Hamstrings", "Glutes"];

export const emptyMmtRow = (muscleGroup = "") => ({
  id: `mmt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  muscleGroup,
  side: "",
  grade: "",
});

export const emptyMmtRows = () =>
  DEFAULT_MMT_MUSCLES.map((muscleGroup) => emptyMmtRow(muscleGroup));

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const coerceExclusiveChoice = (value) => {
  if (Array.isArray(value)) return value.find(Boolean) || "";
  return value == null ? "" : String(value);
};

/** Full editable PT Visit Note payload (JSON on dedicated row). */
export const emptyPtVisitPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  visitDate: "",
  commonHeader: emptyPtNotesCommonHeader(),

  // Visit extras from follow-up screenshot (beyond common header)
  visitTypes: [],
  socDate: "",

  // —— 7. Reason for Visit / Since Last Visit ——
  reasonForVisit: [],
  reasonForVisitOther: "",
  sinceLastVisit: "",

  // —— 8. Subjective ——
  patientReport: "",
  painPreVisit: null,
  painPostVisit: null,
  functionalImprovementSubjective: "",

  // —— 9. Objective ——
  postureObservation: "",
  mmtRows: emptyMmtRows(),
  romKneeExtRight: "",
  romKneeExtLeft: "",
  romKneeFlexRight: "",
  romKneeFlexLeft: "",
  assistiveDevice: "",
  balance: "",
  gaitMobility: [],
  functionalTests: "",

  // —— 10. Interventions Provided ——
  interventions: [],
  interventionOther: "",
  interventionDetails: "",

  // —— 11. Patient Response / Progress ——
  patientResponse: "",
  progressTowardGoals: "",
  functionalImprovementProgress: "",
  responseComments: "",

  // —— 12. Current Functional Status ——
  mobilityTrend: "",
  transfersStatus: "",
  ambulationStatus: "",
  adlsImpacted: [],
  homeSafety: [],

  // —— 13. Goals (dynamic +Add Goal) ——
  shortTermGoals: emptyTextGoals(3, "st"),
  longTermGoals: emptyTextGoals(3, "lt"),

  // —— 14. Plan / Treatment Plan ——
  continuePt: "",
  frequency: "",
  nextVisitDate: "",
  interventionsPlanned: "",

  // —— 15. Additional Notes ——
  additionalNotes: "",

  // —— Signature extras + Supervisory Review ——
  clinicianLicense: "",
  clinicianSignedDate: "",
  supervisoryReviewerName: "",
  supervisoryFunctionalImprovement: "",
  supervisoryDate: "",
});

/** Array keys that must stay arrays after load/merge. */
export const PT_VISIT_ARRAY_KEYS = [
  "visitTypes",
  "reasonForVisit",
  "gaitMobility",
  "interventions",
  "adlsImpacted",
  "homeSafety",
];

/** @deprecated Use emptyPtVisitPayload */
export const emptyRnVisitPayload = emptyPtVisitPayload;
